import React, { useEffect, useState } from "react";
import { strings } from "../../utils/strings";
import { IconButton, Typography } from "@mui/material";
import OTPInput from "react-otp-input";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { useLocation, useNavigate } from "react-router";
import { hashPassword } from "../validator/utils";
import apiService from "../../../service/apiservice/apiService";
import "./pinotp.css";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { useWriteMemory } from "../../generic/hooks/useWriteMemory";
import { publishEvent } from "../../../components/generic/EventPublisher";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import { userDetails } from "../../../components/recoil/userDataRecoil";
import configurationKeys from "../../../service/memory/recoil/featurekeys";
import { executeIntent } from "../../utils/IntentExecutor";
import IntentType from "../../../intentexecutor/utils/IntentType";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import primary_logo from "../../landingpage/assets/pdvimages/logo.svg";
import { iconsData } from "../../../service/memory/recoil/theme";
import { clearLocalStorage } from "../logout/utils";
import { useAuth } from "../../generic/hooks/useAuth";
import { activateIdleTimer } from "../../../components/recoil/activateIdleTimerRecoil";
import { useSearchParams } from "react-router-dom";
import { Space } from "antd";
import { pinEnteredCountAtom } from "../../../components/recoil/pinEnteredCountRecoil";

const PinOtpComponent = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useAuth();
  const icons = useRecoilValue(iconsData);
  const [showPin, setShowPin] = useState(false);
  const [passkey, setPasskey] = useState("");
  const [error, setError] = useState(false);
  const [action, setAction] = useState("");
  const [pinEnteredCount, setPinEnteredCount] = useRecoilState(pinEnteredCountAtom);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const setMemory = useWriteMemory();
  const setConfiguration = useSetRecoilState(currentConfiguration);
  const setUserInformation = useSetRecoilState(userDetails);
  const setActivateIdleTimer = useSetRecoilState(activateIdleTimer);
  const challenge = useReadMemory(MemoryType.CHALLENGE);
  const inactivity = searchParams.get("inactivity");
  const from = location.state?.from;

  const inputStyle = {
    border: "1px solid #CFD3DB",
    borderRadius: 6,
    width: 44,
    height: 44,
    textAlign: "center",
    fontSize: 16,
  };

  const loadConfiguration = async () => {
    try {
      const payload = {
        configurationKey: configurationKeys,
      };
      const response = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload
      );
      const configurationKeysAndValue = {};
      // To ensure that only the configuration has been returned from the configuration store the if
      // condition is present.
      if (configurationKeys.length === response.length) {
        for (const item of response) {
          const configurationValue = item?.configurationValue;
          configurationKeysAndValue[item?.configurationKey] =
            JSON.parse(configurationValue);
        }
        setConfiguration(configurationKeysAndValue);
      }
    } catch (ignored) {}
  };

  const redirectUser = async () => {
    setMemory(MemoryType.USER, user);
    setMemory(MemoryType.AUTH, user);
    publishEvent("niramaya.user.login", user);
    sessionStorage.setItem("sidebarItemSelected", "Dashboard");
    setUserInformation({ ...user });
    setActivateIdleTimer((prev) => !prev);
    await loadConfiguration();

    switch (user?.jobTitle) {
      case "Volunteer":
        if (Boolean(inactivity)) {
          navigate(from, { replace: true });
        } else {
          navigate("/careprovider/homepage", { replace: true });
        }
        break;
      case "Volunteer Coordinator":
        if (Boolean(inactivity)) {
          navigate(from, { replace: true });
        } else {
          navigate("/carecoordinator/homepage", { replace: true });
        }
        break;
      case "Billing Admin":
        if (Boolean(inactivity)) {
          navigate(from, { replace: true });
        } else {
          navigate("/billingAdmin/billing", { replace: true });
        }
        break;
      default:
        navigate("/error?code=1004");
        break;
    }
  };

  const handleMatchPin = async () => {
    if (passkey.length) {
      setLoading(true);
      const hashPasskey = await hashPassword(passkey);

      const payload = {
        passkey: hashPasskey,
        userIdentifier: user?.userIdentifier,
      };
      const response = await apiService.post(
        "niramayafoundation/auth/api/v1/matchpin",
        payload
      );
      setPinEnteredCount(pinEnteredCount + 1);
      const isAuthenticated = response?.value;

      if (isAuthenticated) {
        setPinEnteredCount(0);
        await redirectUser();
      } else if (!isAuthenticated && pinEnteredCount < 2) {
        publishEvent("niramaya.user.login.failed", user, {
          ...user,
          reason: "incorrect_pin",
          attemptsUsed: pinEnteredCount + 1,
          attemptsRemaining: 3 - (pinEnteredCount + 1),
        });
        setError(`Incorrect PIN entered.
                ${
                  pinEnteredCount + 1 &&
                  ` You have ${3 - (pinEnteredCount + 1)} attempt${
                    3 - (pinEnteredCount + 1) === 1 ? "" : "s"
                  } remaining.`
                }`);
        setPasskey("");
      } else {
        publishEvent("niramaya.user.login.failed", user, {
          ...user,
          reason: "pin_attempts_exceeded",
          attemptsUsed: pinEnteredCount + 1,
          errorCode: "1005",
        });
        if (loggedIn) {
          clearLocalStorage();
        }
        setPinEnteredCount(0)
        navigate("/error?code=1005");
      }
      setLoading(false);
    } else {
      setError("Please enter your PIN to continue.");
    }
  };

  const handleSetPin = async () => {
    if (passkey.length === 6) {
      setLoading(true);
      const hashPasskey = await hashPassword(passkey);

      const payload = {
        passkey: hashPasskey,
        userIdentifier: user?.userIdentifier,
      };
      const response = await apiService.post(
        "niramayafoundation/auth/api/v1/setpin",
        payload
      );
      const isAuthenticated = response?.value;
      if (isAuthenticated && !challenge) {
        setModalOpen(true);
      } else {
        redirectUser();
      }
      setLoading(false);
    } else {
      setError("PIN must be 6 digits long.");
    }
  };

  const handlePin = async () => {
    if (action === "matchPin") {
      handleMatchPin();
    } else if (action === "setPin") {
      handleSetPin();
    }
  };

  const registerPasskey = async () => {
    setBiometricLoading(true);
    try {
      const challenge = btoa(crypto.randomUUID());
      const userIdBuffer = new TextEncoder().encode(user?.userIdentifier);
      const userIdBase64 = btoa(
        String.fromCharCode(...new Uint8Array(userIdBuffer))
      );

      const options = {
        challenge,
        rp: { name: window.location.host },
        user: {
          id: userIdBase64,
          name: user?.username,
          displayName: user?.userIdentifier,
        },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
        },
        attestation: "direct",
        timeout: 60000,
      };

      const authResult = await startRegistration(options);
      setMemory(MemoryType.CHALLENGE, authResult);
    } catch (error) {
      console.error("Passkey Registration Failed", error);
    } finally {
      redirectUser();
      setBiometricLoading(false);
    }
  };

  const handleWebAuthn = async () => {
    try {
      const challenge = JSON.parse(localStorage.getItem(MemoryType.CHALLENGE));

      const options = {
        challenge: btoa(crypto.randomUUID()),
        rpId: window.location.hostname,
        userVerification: "required",
        allowCredentials: [
          {
            id: challenge.id,
            type: "public-key",
          },
        ],
      };

      const authResult = await startAuthentication(options);
      if (authResult) {
        await redirectUser();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLoginClick = () => {
    clearLocalStorage();
    navigate("/login");
  };

  const footerButtons = (
    <>
      <SecondaryButton
        buttonText="Maybe Later"
        handleOnClick={redirectUser}
      />
      <PrimaryButton
        buttonText={strings.YES}
        handleOnClick={registerPasskey}
        loading={biometricLoading}
      />
    </>
  );

  useEffect(() => {
    const action = user?.hasPin ? "matchPin" : "setPin";
    setAction(action);
  }, [user?.hasPin]);

  return (
    <>
      <section className="auth-container">
        <Typography
          variant="p"
          className="login-text"
          onClick={handleLoginClick}
        >
          Back to Login Page
        </Typography>
        <div
          className="d-flex justify-content-center align-items-center flex-column p-2"
          style={{ gap: 8 }}
        >
          <span className="navbar-brand d-flex align-items-center navbar-title">
            <img
              width="240"
              src={icons.logo || primary_logo}
              alt="Primary logo"
              loading="lazy"
            />
          </span>
          <Typography variant="h6" textAlign={"center"}>
            {action === "setPin"
              ? "Set up your 6-digit PIN to keep your account safe."
              : "Please authenticate using your 6-digit PIN to unlock the application"}
          </Typography>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handlePin();
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ gap: 6 }}
            >
              <OTPInput
                value={passkey}
                onChange={setPasskey}
                numInputs={6}
                shouldAutoFocus
                inputType={showPin ? "tel" : "password"}
                renderInput={(props) => (
                  <input
                    {...props}
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={1}
                    onInput={(e) => {
                      const val = e.target.value;
                      if (!/^\d$/.test(val)) {
                        e.target.value = "";
                      }
                    }}
                    style={{
                      ...inputStyle,
                      outline: props.style?.outline ?? "none",
                    }}
                  />
                )}
                containerStyle={{
                  justifyContent: "center",
                  gap: 6,
                }}
              />

              <IconButton onClick={() => setShowPin((prev) => !prev)}>
                {showPin ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>

            {error && (
              <div style={{ textAlign: "center" }}>
                <Typography color={"#d32f2f"}>{error}</Typography>
                <Typography color={"#d32f2f"}>
                  If you have forgotten your PIN, please contact your volunteer
                  coordinator to reset it from your profile.
                </Typography>
              </div>
            )}

            <Space
              direction="vertical"
              className="mt-2"
              style={{ width: "100%" }}
              align="center"
            >
              <PrimaryButton
                buttonText={
                  action === "setPin" ? strings.SET_PIN : strings.UNLOCK
                }
                loading={loading}
                buttonDisable={!passkey.length}
                htmlType="submit"
              />

              {challenge &&
                challenge?.userIdentifier === user?.userIdentifier && (
                  <SecondaryButton
                    buttonText={"Use Biometric"}
                    handleOnClick={handleWebAuthn}
                  />
                )}
            </Space>
          </form>
        </div>
      </section>

      {modalOpen && (
        <CustomModal
          closable={false}
          isModalOpen={modalOpen}
          footer={footerButtons}
          closeModal={setModalOpen}
        >
          <div>
            <h5 className="logout-prompt-title">PIN Setup Successful</h5>
            <div>
              Would you like to enable biometric authentication for quicker and
              more secure access?
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default PinOtpComponent;
